type Callback<T> = (inputs: T[]) => Promise<unknown>;

export class PromiseBuffer<T> {
    /**
     * Collected inputs to be flushed on the next iteration
     */
    private inputs: Set<T> = new Set();

    /**
     * Promise for the current in-flight flush
     */
    private inFlightPromise: Promise<unknown> | undefined;

    /**
     * Timeout, which schedules the next flush
     */
    private callbackTimeout: NodeJS.Timeout | undefined;

    /**
     * Timestamp of when the last flush occurred. Used to caculate
     * the delay for subsequent flushes
     */
    private lastFlush: number | undefined;

    /**
     * Callback to invoke with batched inputs when flushing
     */
    private callback: Callback<T>;

    /**
     * Debug flag, enables logging
     */
    private debug: boolean;

    /**
     * Determines how long after enqueueing the buffer gets flushed
     */
    private delay: number;

    /**
     * Determines whether or not the buffer should process values.
     */
    private isStopped = true;

    constructor(options: { callback: Callback<T>; delay: number; debug?: boolean }) {
        const { callback, delay, debug } = options;
        this.callback = callback;
        this.debug = !!debug;
        this.delay = delay;
    }

    getInputsAndClearSet(): T[] {
        const ret = Array.from(this.inputs);
        this.inputs.clear();
        return ret;
    }

    get size(): number {
        return this.inputs.size;
    }

    log(..._args: unknown[]): void {
        // Debug logging disabled - set this.debug to true during development if needed
    }

    maybeFlush(computedDelay: number): void {
        if (
            !this.isStopped &&
            !this.inFlightPromise &&
            !this.callbackTimeout &&
            this.inputs.size > 0
        ) {
            this.log('Enqueued callback with delay', computedDelay);
            this.callbackTimeout = setTimeout(() => {
                this.log('Timeout callback was called');
                this.lastFlush = Date.now();
                this.callbackTimeout = undefined;
                const inputs = [...this.inputs.values()];
                this.inputs.clear();
                this.log('Callback will trigger with inputs', inputs);
                this.inFlightPromise = this.callback(inputs).finally(() => {
                    this.log('Callback finished executing');
                    this.inFlightPromise = undefined;
                    if (this.inputs.size > 0) {
                        const durationSinceLastFlush = !this.lastFlush
                            ? Infinity
                            : Date.now() - this.lastFlush;
                        const nextFlushDelay =
                            durationSinceLastFlush > this.delay
                                ? 0
                                : this.delay - durationSinceLastFlush;
                        this.log(
                            `Found buffered values, next flush will happen in ${nextFlushDelay}ms`,
                            `Last flush happened ${durationSinceLastFlush}ms ago.`
                        );
                        if (!this.isStopped) {
                            this.maybeFlush(nextFlushDelay);
                        }
                    }
                });
            }, computedDelay);
        }
    }

    public stop(): this {
        this.log('Stopping buffer');
        this.isStopped = true;
        clearTimeout(this.callbackTimeout);
        this.callbackTimeout = undefined;
        return this;
    }

    public start(): this {
        this.log('Starting buffer');
        this.isStopped = false;
        this.maybeFlush(this.delay);
        return this;
    }

    public enqueue(input: T): this {
        this.inputs.add(input);
        this.maybeFlush(this.delay);
        return this;
    }

    public reset(): this {
        this.log('Resetting all buffered inputs.');
        this.inputs.clear();
        return this;
    }
}
